/**
 * Thin wrappers around orval-generated Models hooks.
 *
 * The generated hooks return `{data, status, headers}` per orval v8 FETCH
 * client. These wrappers unwrap to a flat shape and centralize cache
 * invalidation so pages don't repeat queryKey plumbing.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { type ApiRequestError, getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import {
  addTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesPost,
  createTeamModelApiInternalTeamsTeamIdModelsPost,
  deleteTeamModelApiInternalTeamsTeamIdModelsModelIdDelete,
  getGetTeamModelFallbackOptionsApiInternalTeamsTeamIdModelsModelIdFallbackOptionsGetQueryKey,
  getListTeamModelsApiInternalTeamsTeamIdModelsGetQueryKey,
  getTeamModelFallbackOptionsApiInternalTeamsTeamIdModelsModelIdFallbackOptionsGet,
  promoteTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesSlugPromotePost,
  removeTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesSlugDelete,
  setTeamDefaultModelApiInternalTeamsTeamIdModelsModelIdDefaultPost,
  updateTeamModelApiInternalTeamsTeamIdModelsModelIdPatch,
} from '@/api/generated/internal/internal'
import {
  addModelAliasApiModelsModelIdAliasesPost,
  createModelApiModelsPost,
  deleteModelApiModelsModelIdDelete,
  getFallbackOptionsApiModelsModelIdFallbackOptionsGet,
  getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryKey,
  getGetModelApiModelsModelIdGetQueryKey,
  getGetModelScalingApiModelsModelIdScalingGetQueryKey,
  getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryKey,
  getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryKey,
  useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet,
  getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryKey,
  getGetTraceLoggingApiModelsTraceLoggingGetQueryKey,
  getListModelsApiModelsGetQueryKey,
  promoteModelAliasApiModelsModelIdAliasesSlugPromotePost,
  removeModelAliasApiModelsModelIdAliasesSlugDelete,
  setDefaultModelApiModelsModelIdDefaultPost,
  updateModelApiModelsModelIdPatch,
  updateModelSchedulesApiModelsModelIdScalingSchedulesPut,
  useGetModelApiModelsModelIdGet,
  useGetModelScalingApiModelsModelIdScalingGet,
  useGetModelScalingChangesApiModelsModelIdScalingChangesGet,
  useGetModelScalingUsageApiModelsModelIdScalingUsageGet,
  useGetModelSchedulesApiModelsModelIdScalingSchedulesGet,
  useGetTraceLoggingApiModelsTraceLoggingGet,
  useListModelsApiModelsGet,
  useSetLatencyAlertsApiModelsModelIdLatencyAlertsPatch,
  useSetTraceLoggingApiModelsTraceLoggingPut,
  useUpdateModelScalingApiModelsModelIdScalingPut,
  useUpdateModelSchedulesApiModelsModelIdScalingSchedulesPut,
  useWakeModelApiModelsModelIdScalingWakePost,
} from '@/api/generated/models/models'
import { useListProxyModelsApiProxyModelsGet } from '@/api/generated/proxy-models/proxy-models'
import { useTeam } from '@/contexts/TeamContext'
import type {
  FallbackOptionsResponse,
  HTTPValidationError,
  LatencyAlertsUpdate,
  ModelAliasCreate,
  ModelCreate,
  ModelEngineMetricsResponse,
  ModelResponse,
  ModelScalingResponse,
  ModelScalingUpdate,
  ModelScalingUsageResponse,
  ModelScheduleWindow,
  ModelScheduleWindowUpdate,
  ModelScheduleWindowUpdateWeekdaysItem,
  ModelSchedulesResponse,
  ModelSchedulesUpdate,
  ModelUpdate,
  ModelWindowUsage,
  ProxyModelListItem,
  ReplicaSettingChangeResponse,
  TraceLoggingModelUpdate,
  TraceLoggingState,
} from '@/api/generated/model'

export type Model = ModelResponse
/** A day of the week as Baseten's schedule API spells it (`MONDAY`..`SUNDAY`). */
export type Weekday = ModelScheduleWindowUpdateWeekdaysItem
export type {
  FallbackOptionsResponse,
  ModelCreate,
  ModelEngineMetricsResponse,
  ModelScalingResponse,
  ModelScalingUpdate,
  ModelScalingUsageResponse,
  ModelScheduleWindow,
  ModelScheduleWindowUpdate,
  ModelSchedulesResponse,
  ModelSchedulesUpdate,
  ModelWindowUsage,
  ProxyModelListItem,
  ReplicaSettingChangeResponse,
}

/**
 * Whose models a hook reads and writes. `team` is the signed-in team, via
 * /api/models and the team header. `staff` is a TrainLoop employee
 * configuring the team named by id, via /api/internal/teams/{id}/models,
 * where the API applies the same rules with the team taken from the URL.
 * The two share the form, the dialogs and these hooks; only the request
 * differs, so every model mutation takes its scope explicitly.
 */
export type ModelScope = { kind: 'team' } | { kind: 'staff'; teamId: string }

export const TEAM_SCOPE: ModelScope = { kind: 'team' }

function modelsListKey(scope: ModelScope): readonly unknown[] {
  return scope.kind === 'staff'
    ? getListTeamModelsApiInternalTeamsTeamIdModelsGetQueryKey(scope.teamId)
    : getListModelsApiModelsGetQueryKey()
}

function fallbackOptionsKey(scope: ModelScope, modelId: string): readonly unknown[] {
  return scope.kind === 'staff'
    ? getGetTeamModelFallbackOptionsApiInternalTeamsTeamIdModelsModelIdFallbackOptionsGetQueryKey(
        scope.teamId,
        modelId,
      )
    : getGetFallbackOptionsApiModelsModelIdFallbackOptionsGetQueryKey(modelId)
}

/**
 * The shape every model-returning call resolves to, on both scopes: the
 * generated response unions are `{data, status}` pairs keyed by status.
 */
type ModelResult =
  | { data: ModelResponse; status: 200 | 201 }
  | { data: HTTPValidationError; status: 422 }

/**
 * The model on the success path. `orvalFetch` throws on any non-2xx
 * status, so a result that reaches a mutation's caller always carries the
 * model; the status check is what narrows the type.
 */
function modelFrom(result: ModelResult): ModelResponse {
  if (result.status === 422) {
    throw new Error('A validation error reached the success path')
  }
  return result.data
}

// A write changes the model's routes, so the list, the customer detail
// read and the edit dialog's fallback options (TRA-1154) all go stale.
// Orval keys are single-element URL arrays, so the detail key is NOT a
// prefix of the fallback-options key; without the last one a reopened edit
// dialog renders the pre-save chain (and pre-repoint primary) for the
// 5-minute staleTime and can write it back wholesale.
function invalidateModel(queryClient: QueryClient, scope: ModelScope, modelId: string): void {
  void queryClient.invalidateQueries({ queryKey: modelsListKey(scope) })
  if (scope.kind === 'team') {
    void queryClient.invalidateQueries({
      queryKey: getGetModelApiModelsModelIdGetQueryKey(modelId),
    })
  }
  void queryClient.invalidateQueries({ queryKey: fallbackOptionsKey(scope, modelId) })
}

export interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useModels(): UnwrappedQuery<Model[]> {
  const query = useListModelsApiModelsGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useModel(modelId: string | undefined): UnwrappedQuery<Model> {
  const query = useGetModelApiModelsModelIdGet(modelId ?? '', {
    query: { enabled: !!modelId },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useProxyModels(): UnwrappedQuery<ProxyModelListItem[]> {
  const query = useListProxyModelsApiProxyModelsGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** The fallback-options read on either scope, keyed by status like ModelResult. */
type FallbackOptionsResult =
  | { data: FallbackOptionsResponse; status: 200 }
  | { data: HTTPValidationError; status: 422 }

/**
 * A model's live gateway routing: route, fallback chain, serving deployment
 * and the params the gateway forces on it. Refetches every 60s, like the
 * scaling read below. The gateway's admin API changes all of this with no
 * platform mutation to invalidate the cache, and window-focus refetch is
 * off app-wide, so without the interval an open model page would keep
 * showing the params it first loaded (TRA-1411).
 */
export function useFallbackOptions(
  modelId: string | undefined,
  scope: ModelScope,
): UnwrappedQuery<FallbackOptionsResponse> {
  const id = modelId ?? ''
  const query = useQuery<FallbackOptionsResult, ApiRequestError>({
    queryKey: fallbackOptionsKey(scope, id),
    queryFn: (): Promise<FallbackOptionsResult> =>
      scope.kind === 'staff'
        ? getTeamModelFallbackOptionsApiInternalTeamsTeamIdModelsModelIdFallbackOptionsGet(
            scope.teamId,
            id,
          )
        : getFallbackOptionsApiModelsModelIdFallbackOptionsGet(id),
    enabled: !!modelId,
    refetchInterval: 60_000,
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * Live Baseten scaling state (TRA-1051). Refetches every 60s so the replica
 * count on screen stays current; each fetch reads the Baseten management API
 * through the gateway, not a cache.
 */
export function useModelScaling(
  modelId: string | undefined,
  enabled: boolean,
  hours: number,
): UnwrappedQuery<ModelScalingResponse> {
  const query = useGetModelScalingApiModelsModelIdScalingGet(
    modelId ?? '',
    { hours },
    { query: { enabled: !!modelId && enabled, refetchInterval: 60_000 } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export interface EngineMetricsQuery extends UnwrappedQuery<ModelEngineMetricsResponse> {
  /** `data` is the previous window's, kept while this one loads. */
  isPlaceholderData: boolean
}

/**
 * The gateway's vLLM engine history for a dedicated Baseten model
 * (TRA-1484). Read once per window: the samples land every 2 minutes and
 * the page is a look back, not a live view. A move to a longer window
 * (TRA-1592) keeps the previous one on screen until the new one lands.
 */
export function useModelEngineMetrics(
  modelId: string | undefined,
  enabled: boolean,
  hours: number,
): EngineMetricsQuery {
  const query = useGetModelEngineMetricsApiModelsModelIdEngineMetricsGet(
    modelId ?? '',
    { hours },
    { query: { enabled: !!modelId && enabled, placeholderData: keepPreviousData } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isPlaceholderData: query.isPlaceholderData,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The gateway samples the load every 2 minutes, so polling faster than
 * this only adds 7-day usage reads on the gateway's database.
 */
export const SCALING_USAGE_POLL_MS = 5 * 60_000

/**
 * How much of the running replicas the traffic used over the last `hours`,
 * as load steps, and how much of each schedule window was used (TRA-1437).
 * The chart and the window table draw it beside the replica samples. A save of the
 * autoscaling settings or the windows reloads it at once (`invalidateScaling`).
 */
export function useModelScalingUsage(
  modelId: string | undefined,
  enabled: boolean,
  hours: number,
): UnwrappedQuery<ModelScalingUsageResponse> {
  const query = useGetModelScalingUsageApiModelsModelIdScalingUsageGet(
    modelId ?? '',
    { hours },
    { query: { enabled: !!modelId && enabled, refetchInterval: SCALING_USAGE_POLL_MS } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** How many saved changes the model page lists (TRA-1558). */
export const RECENT_CHANGES_LIMIT = 10
/**
 * How many the replicas chart marks: the route's cap. More than that in the
 * stretch shown is rare (a change every 100 minutes for a week); the chart
 * says when the newest ones are all it got (`marksCapped`).
 */
export const CHART_CHANGES_LIMIT = 100

/**
 * The latest changes of the model's replica settings (TRA-1558): who changed
 * the regular range or the windows, when, and what changed, including the
 * changes the api's checker found on Baseten's side. A save of either
 * reloads it (`invalidateScaling`); it also polls every minute, like the
 * schedule query, so a teammate's save or a Baseten change shows up on an
 * open page.
 */
export function useModelScalingChanges(
  modelId: string | undefined,
  enabled: boolean,
  limit: number,
): UnwrappedQuery<ReplicaSettingChangeResponse[]> {
  const query = useGetModelScalingChangesApiModelsModelIdScalingChangesGet(
    modelId ?? '',
    { limit },
    { query: { enabled: !!modelId && enabled, refetchInterval: 60_000 } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The replica chart and the usage stripes on it (TRA-1437) both follow
 * the autoscaling knobs and the windows, so a save of either refreshes both,
 * and the list of saved changes (TRA-1558) with them. The usage and changes
 * keys are matched without their params so every window size reloads.
 */
function invalidateScaling(queryClient: QueryClient, modelId: string): void {
  void queryClient.invalidateQueries({
    queryKey: getGetModelScalingApiModelsModelIdScalingGetQueryKey(modelId),
  })
  void queryClient.invalidateQueries({
    queryKey: getGetModelScalingUsageApiModelsModelIdScalingUsageGetQueryKey(modelId),
  })
  void queryClient.invalidateQueries({
    queryKey: getGetModelScalingChangesApiModelsModelIdScalingChangesGetQueryKey(modelId),
  })
}

export function useUpdateModelScaling(): UseMutationResult<
  unknown,
  ApiRequestError,
  { modelId: string; data: ModelScalingUpdate }
> {
  const queryClient = useQueryClient()
  return useUpdateModelScalingApiModelsModelIdScalingPut({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateScaling(queryClient, variables.modelId)
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? 'Autoscaling update failed. Try again',
        )
      },
    },
  })
}

export function useWakeModel(): UseMutationResult<
  unknown,
  ApiRequestError,
  { modelId: string }
> {
  const queryClient = useQueryClient()
  return useWakeModelApiModelsModelIdScalingWakePost({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getGetModelScalingApiModelsModelIdScalingGetQueryKey(
            variables.modelId,
          ),
        })
      },
      onError: (error) => {
        toast.error(getApiErrorDetail(error) ?? 'Wake request failed. Try again')
      },
    },
  })
}

/**
 * The schedule windows of a dedicated Baseten model (TRA-1426), read live
 * through the gateway. Refetches every 60s, like the scaling state beside it,
 * so the window marked active is the one in force.
 */
export function useModelSchedules(
  modelId: string | undefined,
  enabled: boolean,
): UnwrappedQuery<ModelSchedulesResponse> {
  const query = useGetModelSchedulesApiModelsModelIdScalingSchedulesGet(
    modelId ?? '',
    { query: { enabled: !!modelId && enabled, refetchInterval: 60_000 } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/**
 * The api refused a schedule save because the schedule changed since the
 * page read it (409). Nothing was written.
 */
export function isScheduleConflict(error: unknown): boolean {
  return getApiErrorStatus(error) === 409
}

type SavedSchedules = Awaited<
  ReturnType<typeof updateModelSchedulesApiModelsModelIdScalingSchedulesPut>
>

interface ScheduleSaveCallbacks {
  onMutate: (variables: { modelId: string }) => Promise<void>
  onSuccess: (saved: SavedSchedules, variables: { modelId: string }) => Promise<void>
  onError: (error: ApiRequestError, variables: { modelId: string }) => void
}

/**
 * What a schedule save does to the cache. A save sends the FULL list of
 * weekday windows, built from the cached read, so the next save must never
 * start from a list older than the last one:
 *
 * - The api answers a save with the schedule re-read, and that answer goes
 *   straight into the cache. Waiting for a refetch instead leaves a moment
 *   where the controls are live over the old list, and a second quick edit
 *   would send it back and undo the first.
 * - A read in flight is cancelled before the save and again before its
 *   answer is stored, so a poll that started earlier cannot land afterwards
 *   with the list from before.
 * - A 409 means the schedule changed somewhere else since this page read it
 *   (the api refuses the save and writes nothing): reload it.
 *
 * The replica badge and chart are refreshed too, since the windows decide
 * how many replicas run. Exported for its test.
 */
export function scheduleSaveCallbacks(queryClient: QueryClient): ScheduleSaveCallbacks {
  const schedulesKey = getGetModelSchedulesApiModelsModelIdScalingSchedulesGetQueryKey
  return {
    onMutate: async ({ modelId }) => {
      await queryClient.cancelQueries({ queryKey: schedulesKey(modelId) })
    },
    onSuccess: async (saved, { modelId }) => {
      await queryClient.cancelQueries({ queryKey: schedulesKey(modelId) })
      queryClient.setQueryData(schedulesKey(modelId), saved)
      invalidateScaling(queryClient, modelId)
    },
    onError: (error, { modelId }) => {
      // The api's detail is written for the person saving (Baseten's own
      // sentence for a rejected window, relayed as is), so it is the toast.
      toast.error(getApiErrorDetail(error) ?? 'Schedule update failed. Try again')
      if (isScheduleConflict(error)) {
        void queryClient.invalidateQueries({ queryKey: schedulesKey(modelId) })
      }
    },
  }
}

/** Save the full list of weekday windows (see scheduleSaveCallbacks). */
export function useUpdateModelSchedules(): UseMutationResult<
  unknown,
  ApiRequestError,
  { modelId: string; data: ModelSchedulesUpdate }
> {
  const queryClient = useQueryClient()
  return useUpdateModelSchedulesApiModelsModelIdScalingSchedulesPut({
    mutation: scheduleSaveCallbacks(queryClient),
  })
}

export function useCreateModel(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { data: ModelCreate }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ data }: { data: ModelCreate }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? createTeamModelApiInternalTeamsTeamIdModelsPost(scope.teamId, data)
          : createModelApiModelsPost(data)),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: modelsListKey(scope) })
    },
  })
}

export function useUpdateModel(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { modelId: string; data: ModelUpdate }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ modelId, data }: { modelId: string; data: ModelUpdate }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? updateTeamModelApiInternalTeamsTeamIdModelsModelIdPatch(scope.teamId, modelId, data)
          : updateModelApiModelsModelIdPatch(modelId, data)),
      ),
    onSuccess: (_data, variables) => {
      invalidateModel(queryClient, scope, variables.modelId)
    },
  })
}

export function useTraceLogging(): UnwrappedQuery<TraceLoggingState> {
  const query = useGetTraceLoggingApiModelsTraceLoggingGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useSetTraceLogging(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: TraceLoggingModelUpdate }
> {
  const queryClient = useQueryClient()
  return useSetTraceLoggingApiModelsTraceLoggingPut({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTraceLoggingApiModelsTraceLoggingGetQueryKey(),
        })
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? 'Trace logging update failed. Try again',
        )
      },
    },
  })
}

/**
 * The per-model latency-alerts switch (TRA-1414). The flag rides on the
 * model row, so the list and the detail read are both stale after a flip.
 */
export function useSetLatencyAlerts(): UseMutationResult<
  unknown,
  ApiRequestError,
  { modelId: string; data: LatencyAlertsUpdate }
> {
  const queryClient = useQueryClient()
  return useSetLatencyAlertsApiModelsModelIdLatencyAlertsPatch({
    mutation: {
      onSuccess: (_data, variables) => {
        invalidateModel(queryClient, TEAM_SCOPE, variables.modelId)
      },
      onError: (error) => {
        toast.error(
          getApiErrorDetail(error) ?? 'Latency alerts were not changed. Try again',
        )
      },
    },
  })
}

export function useDeleteModel(
  scope: ModelScope,
): UseMutationResult<unknown, ApiRequestError, { modelId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ modelId }: { modelId: string }) =>
      scope.kind === 'staff'
        ? deleteTeamModelApiInternalTeamsTeamIdModelsModelIdDelete(scope.teamId, modelId)
        : deleteModelApiModelsModelIdDelete(modelId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: modelsListKey(scope) })
    },
  })
}

export function useAddModelAlias(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { modelId: string; data: ModelAliasCreate }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ modelId, data }: { modelId: string; data: ModelAliasCreate }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? addTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesPost(
              scope.teamId,
              modelId,
              data,
            )
          : addModelAliasApiModelsModelIdAliasesPost(modelId, data)),
      ),
    onSuccess: (_result, variables) => {
      invalidateModel(queryClient, scope, variables.modelId)
    },
  })
}

export function useRemoveModelAlias(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { modelId: string; slug: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ modelId, slug }: { modelId: string; slug: string }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? removeTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesSlugDelete(
              scope.teamId,
              modelId,
              slug,
            )
          : removeModelAliasApiModelsModelIdAliasesSlugDelete(modelId, slug)),
      ),
    onSuccess: (_result, variables) => {
      invalidateModel(queryClient, scope, variables.modelId)
    },
  })
}

export function usePromoteModelAlias(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { modelId: string; slug: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ modelId, slug }: { modelId: string; slug: string }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? promoteTeamModelAliasApiInternalTeamsTeamIdModelsModelIdAliasesSlugPromotePost(
              scope.teamId,
              modelId,
              slug,
            )
          : promoteModelAliasApiModelsModelIdAliasesSlugPromotePost(modelId, slug)),
      ),
    onSuccess: (_result, variables) => {
      invalidateModel(queryClient, scope, variables.modelId)
    },
  })
}

/**
 * Every customer model read: the list and each detail. Their keys carry no
 * team id (the team rides in a header), so call this only for the active
 * team's models.
 */
function invalidateCustomerModels(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0]
      return typeof key === 'string' && (key === '/api/models' || /^\/api\/models\/[^/]+$/.test(key))
    },
  })
}

/**
 * Make a model the team's default (TRA-1215), the one the nightly evals
 * score. The flag leaves the previous default in the same call, so that
 * model's cached detail is stale too: every customer model read is
 * invalidated, not only this one's. A staff change refetches the staff
 * list, and the customer reads as well when the team is the one this tab
 * shows (a staff member who is also on the team).
 */
export function useSetDefaultModel(
  scope: ModelScope,
): UseMutationResult<ModelResponse, ApiRequestError, { modelId: string }> {
  const queryClient = useQueryClient()
  const { activeTeamId } = useTeam()
  return useMutation({
    mutationFn: async ({ modelId }: { modelId: string }) =>
      modelFrom(
        await (scope.kind === 'staff'
          ? setTeamDefaultModelApiInternalTeamsTeamIdModelsModelIdDefaultPost(scope.teamId, modelId)
          : setDefaultModelApiModelsModelIdDefaultPost(modelId)),
      ),
    onSuccess: () => {
      if (scope.kind === 'staff') {
        void queryClient.invalidateQueries({ queryKey: modelsListKey(scope) })
        if (scope.teamId !== activeTeamId) return
      }
      invalidateCustomerModels(queryClient)
    },
    onError: (error) => {
      toast.error(getApiErrorDetail(error) ?? 'The default model was not changed. Try again')
    },
  })
}
